import { useCallback, useEffect, useRef, useState } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import { Bot, Loader2, Mic, MicOff, PhoneOff, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

type Phase = 'connecting' | 'live' | 'ended' | 'error';

interface LiveTurn {
  role: string;
  content: string;
}

const PHASE_LABEL: Record<Phase, string> = {
  connecting: 'Conectando…',
  live: 'Em ligação',
  ended: 'Ligação encerrada',
  error: 'Falhou',
};

/**
 * Conduz uma ligação pelo navegador: abre o microfone, conecta no agente e mostra a
 * transcrição em tempo real. O token vale para uma única chamada e expira em ~30 s,
 * por isso a conexão começa assim que o componente monta.
 */
export function WebCallPanel({
  accessToken,
  onEnded,
}: {
  accessToken: string;
  onEnded?: () => void;
}) {
  const clientRef = useRef<RetellWebClient | null>(null);
  const teardownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const [phase, setPhase] = useState<Phase>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [agentTalking, setAgentTalking] = useState(false);
  const [turns, setTurns] = useState<LiveTurn[]>([]);

  useEffect(() => {
    // O React em StrictMode monta o efeito, limpa e monta de novo. O token do Retell vale
    // para UMA ligação: se a segunda montagem criasse outro cliente, a primeira conexão
    // derrubaria a sala e a ligação morreria antes de começar. Por isso o desligamento é
    // adiado um tick e cancelado quando a remontagem vem logo em seguida.
    if (teardownRef.current) {
      clearTimeout(teardownRef.current);
      teardownRef.current = null;
    }

    if (!clientRef.current) {
      const client = new RetellWebClient();
      clientRef.current = client;

      // Só avança de "conectando"; eventos atrasados não ressuscitam uma ligação encerrada.
      const goLive = () => setPhase((p) => (p === 'connecting' ? 'live' : p));
      client.on('call_started', goLive);
      client.on('call_ready', goLive);
      client.on('agent_start_talking', () => setAgentTalking(true));
      client.on('agent_stop_talking', () => setAgentTalking(false));
      client.on('update', (u: { transcript?: LiveTurn[] }) => {
        if (Array.isArray(u?.transcript)) setTurns(u.transcript);
      });
      client.on('call_ended', () => {
        setAgentTalking(false);
        setPhase((p) => (p === 'error' ? p : 'ended'));
        onEndedRef.current?.();
      });
      client.on('error', (e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        setPhase('error');
        client.stopCall();
      });

      void client.startCall({ accessToken }).catch((e: unknown) => {
        setError(
          e instanceof Error
            ? e.message
            : 'Não foi possível abrir o microfone. Autorize o acesso no navegador e ligue de novo.',
        );
        setPhase('error');
      });
    }

    return () => {
      teardownRef.current = setTimeout(() => {
        teardownRef.current = null;
        clientRef.current?.removeAllListeners();
        clientRef.current?.stopCall();
        clientRef.current = null;
      }, 0);
    };
  }, [accessToken]);

  const toggleMute = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    setMuted((m) => {
      if (m) client.unmute();
      else client.mute();
      return !m;
    });
  }, []);

  const hangUp = useCallback(() => {
    clientRef.current?.stopCall();
    setPhase('ended');
    onEndedRef.current?.();
  }, []);

  const active = phase === 'connecting' || phase === 'live';

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <span
            className={`size-2 shrink-0 rounded-full ${
              phase === 'live'
                ? 'animate-pulse bg-emerald-500'
                : phase === 'connecting'
                  ? 'animate-pulse bg-amber-500'
                  : phase === 'error'
                    ? 'bg-destructive'
                    : 'bg-muted-foreground'
            }`}
          />
          Ligação pelo navegador · {PHASE_LABEL[phase]}
          {agentTalking && (
            <span className="text-xs font-normal text-muted-foreground">a IA está falando…</span>
          )}
        </CardTitle>
        {active && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={toggleMute}>
              {muted ? <MicOff /> : <Mic />} {muted ? 'Reativar' : 'Mudo'}
            </Button>
            <Button variant="destructive" size="sm" onClick={hangUp}>
              <PhoneOff /> Encerrar
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {phase === 'connecting' && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Autorize o microfone e aguarde a IA falar.
          </p>
        )}
        {phase === 'ended' && (
          <p className="text-sm text-muted-foreground">
            Transcrição, sentimento e resultado aparecem abaixo assim que a análise chegar (10–30
            s).
          </p>
        )}
        {turns.length > 0 && (
          <ol className="mt-3 max-h-72 space-y-3 overflow-y-auto">
            {turns
              .filter((t) => t.content)
              .map((t, i) => (
                <li key={i} className={`flex gap-3 ${t.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`flex size-7 shrink-0 items-center justify-center rounded-full ${
                      t.role === 'user' ? 'bg-secondary' : 'bg-primary/15 text-primary'
                    }`}
                  >
                    {t.role === 'user' ? (
                      <UserIcon className="size-4" />
                    ) : (
                      <Bot className="size-4" />
                    )}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      t.role === 'user' ? 'bg-secondary' : 'bg-primary/10'
                    }`}
                  >
                    {t.content}
                  </div>
                </li>
              ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
